import React, { useState } from 'react';
import { Download } from 'lucide-react';
import { useHealthForm } from '@/hooks/useHealthForm';
import { submitHealthForm } from '@/lib/emailService';
import { downloadHealthDeclarationPDF } from '@/lib/pdfExport';
import { PersonalInfoSection } from './PersonalInfoSection';
import { MedicalQuestionnaire } from './MedicalQuestionnaire';
import { DeclarationSection } from './DeclarationSection';

interface HealthDeclarationFormProps {
  trainerEmail?: string;
  onSuccess?: () => void;
}

export const HealthDeclarationForm: React.FC<HealthDeclarationFormProps> = ({
  trainerEmail = 'sharon@sharon.fitness',
  onSuccess,
}) => {
  const {
    formData,
    errors,
    isSubmitting,
    submitStatus,
    submitMessage,
    updateField,
    validateEntireForm,
    resetForm,
    submitForm,
  } = useHealthForm();

  const [currentStep, setCurrentStep] = useState<'personal' | 'medical' | 'declaration'>(
    'personal'
  );

  const handleNext = () => {
    if (currentStep === 'personal') {
      // Validate personal info
      const personalFields = ['firstName', 'lastName', 'dateOfBirth', 'phone', 'email'];
      let isValid = true;

      personalFields.forEach((field) => {
        const error = validateField(field, formData[field as keyof typeof formData]);
        if (error) isValid = false;
      });

      if (isValid) {
        setCurrentStep('medical');
      }
    } else if (currentStep === 'medical') {
      setCurrentStep('declaration');
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'medical') {
      setCurrentStep('personal');
    } else if (currentStep === 'declaration') {
      setCurrentStep('medical');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await submitForm(async (data) => {
      await submitHealthForm(data, trainerEmail);
    });

    if (success) {
      setCurrentStep('personal');
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  // Helper function for validation
  const validateField = (field: string, value: any): string | null => {
    if (!value && ['firstName', 'lastName', 'dateOfBirth', 'phone', 'email'].includes(field)) {
      return 'שדה זה הוא חובה';
    }
    return null;
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-4xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8 flex justify-center gap-4">
        <div
          className={`w-3 h-3 rounded-full transition-all ${
            currentStep === 'declaration' ? 'bg-[#C17A4A]' : 'bg-[#E8A87C]'
          }`}
        />
        <div
          className={`w-3 h-3 rounded-full transition-all ${
            currentStep === 'medical' ? 'bg-[#C17A4A]' : 'bg-[#E8A87C]'
          }`}
        />
        <div
          className={`w-3 h-3 rounded-full transition-all ${
            currentStep === 'personal' ? 'bg-[#C17A4A]' : 'bg-[#E8A87C]'
          }`}
        />
      </div>

      {/* Form Sections */}
      <div className="bg-white rounded-lg p-8 shadow-md">
        {currentStep === 'personal' && (
          <PersonalInfoSection data={formData} errors={errors} onChange={updateField} />
        )}

        {currentStep === 'medical' && (
          <MedicalQuestionnaire data={formData} errors={errors} onChange={updateField} />
        )}

        {currentStep === 'declaration' && (
          <DeclarationSection data={formData} errors={errors} onChange={updateField} />
        )}

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="mt-6 bg-green-50 border-2 border-green-500 rounded-lg p-4">
            <p className="text-green-600 text-sm text-right font-medium">{submitMessage}</p>
            <button
              type="button"
              onClick={() => downloadHealthDeclarationPDF(formData)}
              className="mt-4 flex items-center justify-center gap-2 w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <Download size={18} />
              הורד עותק חתום (PDF)
            </button>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-6 bg-red-50 border-2 border-red-500 rounded-lg p-4">
            <p className="text-red-600 text-sm text-right">{submitMessage}</p>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between gap-4">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={currentStep === 'personal'}
          className="px-6 py-3 rounded-lg border-2 border-[#E8A87C] text-[#1B4332] font-medium hover:bg-[#FFF8F0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          חזור
        </button>

        {currentStep !== 'declaration' ? (
          <button
            type="button"
            onClick={handleNext}
            className="px-6 py-3 rounded-lg bg-[#C17A4A] text-white font-medium hover:bg-[#B8704A] transition-colors"
          >
            הבא
          </button>
        ) : (
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 rounded-lg bg-[#C17A4A] text-white font-medium hover:bg-[#B8704A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin">⏳</span>
                שולח...
              </>
            ) : (
              'שלח הצהרה'
            )}
          </button>
        )}
      </div>
    </form>
  );
};
